import { useState, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import DigitalHumanStore, { CourseCardOverlayItem } from '@/stores/digital-human-store';

interface GoalCardsOverlayProps {
  store: DigitalHumanStore;
  onCourseConfirmed: (course: CourseCardOverlayItem, goalIndex: number) => void;
}

export const GoalCardsOverlay = observer(({ store, onCourseConfirmed }: GoalCardsOverlayProps) => {
  const [flippedCourseIds, setFlippedCourseIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    const onFlipCourse = (event: Event) => {
      const customEvent = event as CustomEvent<{ courseId?: string }>;
      const courseId = typeof customEvent.detail?.courseId === 'string' ? customEvent.detail.courseId.trim() : '';
      if (courseId) {
        setFlippedCourseIds(prev => {
          const next = new Set(prev);
          next.add(courseId);
          return next;
        });
      }
    };
    window.addEventListener('metro-flip-course', onFlipCourse as EventListener);
    return () => window.removeEventListener('metro-flip-course', onFlipCourse as EventListener);
  }, []);

  if (!store.goalCardsVisible || store.goalCards.length === 0) return null;

  const handleFlip = (courseId: string) => {
    setFlippedCourseIds(prev => {
      const next = new Set(prev);
      if (next.has(courseId)) {
        next.delete(courseId);
      } else {
        next.add(courseId);
      }
      return next;
    });
  };

  const handleConfirm = (course: CourseCardOverlayItem, goalIndex: number) => {
    setFlippedCourseIds(new Set());
    onCourseConfirmed(course, goalIndex);
  };

  return (
    <div className="pointer-events-none absolute inset-x-0 bottom-48 z-[70] flex flex-row items-end justify-evenly px-6">
      {store.goalCards.map((goal, goalIndex) => {
        const coursesForGoal = store.courseCardsVisible
          ? store.courseCards.filter(c => c.goalIndex === goalIndex)
          : [];
        const hasSelected = !!goal.selectedCourseId;

        return (
          <div key={goal.goalId} className="flex flex-col items-center gap-2">
            {/* Selected course — compact, hidden when new course cards are pending for this goal */}
            {hasSelected && coursesForGoal.length === 0 && (
              <div className="animate-slide-up w-96 rounded-xl border border-violet-400/50 bg-violet-500/15 px-5 py-3 text-white shadow-lg backdrop-blur-md">
                <div className="flex items-center gap-2">
                  <div className="h-0.5 w-6 flex-shrink-0 rounded-full bg-violet-300" />
                  <span className="flex-1 text-sm font-semibold leading-snug">
                    {goal.selectedCourseTitle}
                  </span>
                  <svg className="h-4 w-4 flex-shrink-0 text-violet-300" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
              </div>
            )}

            {/* Course options for this goal */}
            {coursesForGoal.length > 0 && (
              <div
                className={`flex flex-col gap-2 transition-opacity duration-500 ${
                  store.courseCardsFading ? 'opacity-0' : 'opacity-100'
                }`}
              >
                {coursesForGoal.map((course, idx) => {
                  const isFlipped = flippedCourseIds.has(course.courseId);

                  return (
                    <div
                      key={`${course.courseId}-${idx}`}
                      className="pointer-events-auto w-[28rem]"
                      style={{ perspective: '1000px' }}
                    >
                      {/* Grid container: both faces overlap in the same cell so the
                          card auto-sizes to whichever face is taller */}
                      <div
                        className={`grid cursor-pointer transition-all duration-500 [transform-style:preserve-3d] ${
                          isFlipped ? '[transform:rotateY(180deg)]' : ''
                        }`}
                        style={{ animationDelay: `${idx * 0.08}s` }}
                        onClick={() => handleFlip(course.courseId)}
                      >
                        {/* Front face — centered in whatever height the back dictates */}
                        <div className="animate-slide-up col-start-1 row-start-1 flex flex-col justify-center rounded-2xl border border-white/20 bg-white/10 px-6 py-5 text-white shadow-xl backdrop-blur-md [backface-visibility:hidden]">
                          <div className="flex items-center justify-start gap-3">
                            <div className="h-0.5 w-10 flex-shrink-0 rounded-full bg-violet-400" />
                            <h3 className="text-base font-semibold leading-snug">{course.title}</h3>
                          </div>
                        </div>

                        {/* Back face — full description, sets card height */}
                        <div className="col-start-1 row-start-1 flex flex-col justify-between rounded-2xl border border-violet-400/50 bg-violet-950/90 px-5 py-4 text-white shadow-xl backdrop-blur-md [backface-visibility:hidden] [transform:rotateY(180deg)]">
                          <p className="text-sm leading-relaxed text-white/85">
                            {course.summary}
                          </p>
                          <button
                            className="pointer-events-auto mt-3 flex items-center justify-center gap-2 self-end rounded-full border border-violet-400/70 bg-violet-500/25 px-4 py-1.5 text-sm font-semibold text-violet-200 transition-colors hover:bg-violet-500/45"
                            onClick={e => {
                              e.stopPropagation();
                              handleConfirm(course, goalIndex);
                            }}
                          >
                            <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                            Diesen Kurs wählen
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Goal card */}
            <div
              className="animate-slide-up flex min-h-[5.5rem] w-[28rem] flex-col justify-center rounded-2xl border border-violet-800/60 bg-violet-950/80 px-6 py-5 text-white shadow-xl backdrop-blur-md"
              style={{ animationDelay: `${goalIndex * 0.1}s` }}
            >
              <div className="flex items-center justify-start gap-3">
                <div className="h-0.5 w-10 flex-shrink-0 rounded-full bg-violet-400" />
                <h3 className="text-base font-semibold leading-snug">{goal.title}</h3>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
});
